import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1f988948-8d96-469b-9503-c5534672fbb7/artifacts/ygf96dzb_juiceapp%20logo.png";
const APP_STORE_URL = "https://apps.apple.com/fr/app/juiceapp-cold-press-ai/id1166117781?l=en-GB";

const languages = [
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'es', name: 'Español', flag: 'ES' }
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();

  const currentLang = languages.find(l => l.code === language);
  const langPrefix = language !== 'en' ? `/${language}` : '';

  return (
    <nav className="sticky top-0 z-50 bg-[#fafafa]/80 backdrop-blur-xl border-b border-[#e2e8f0]/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to={`${langPrefix}/`} className="flex items-center gap-3" data-testid="logo-link">
            <img src={LOGO_URL} alt="JuiceApp" className="h-8 w-8 md:h-10 md:w-10" />
            <span className="font-heading text-xl md:text-2xl font-semibold tracking-tight text-[#0f172a]">
              JuiceApp
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to={`${langPrefix}/`} 
              className="text-sm font-body font-medium tracking-wide text-[#475569] hover:text-[#0f172a] transition-colors"
              data-testid="nav-home"
            >
              {t('nav.home')}
            </Link>
            <Link 
              to={`${langPrefix}/blog`} 
              className="text-sm font-body font-medium tracking-wide text-[#475569] hover:text-[#0f172a] transition-colors"
              data-testid="nav-blog"
            >
              {t('nav.blog')}
            </Link>
            
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 text-sm font-body font-medium tracking-wide text-[#475569] hover:text-[#0f172a] transition-colors" data-testid="language-switcher">
                {currentLang?.flag}
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      // Navigate to language-prefixed route
                      const currentPath = window.location.pathname;
                      // Remove any existing language prefix
                      const cleanPath = currentPath.replace(/^\/(fr|es)/, '') || '/';
                      const newPrefix = lang.code !== 'en' ? `/${lang.code}` : '';
                      navigate(`${newPrefix}${cleanPath}`);
                    }}
                    className={language === lang.code ? 'bg-[#dcfce7]' : ''}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Download Button */}
            <a 
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#16a34a] text-white rounded-full text-sm font-medium hover:bg-[#15803d] transition-colors"
              data-testid="download-btn-desktop"
            >
              {t('nav.download')}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-4"
          >
            <div className="flex flex-col gap-4">
              <Link 
                to={`${langPrefix}/`} 
                className="text-sm font-body font-medium text-[#475569] hover:text-[#0f172a] transition-colors py-2"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-nav-home"
              >
                {t('nav.home')}
              </Link>
              <Link 
                to={`${langPrefix}/blog`} 
                className="text-sm font-body font-medium text-[#475569] hover:text-[#0f172a] transition-colors py-2"
                onClick={() => setIsOpen(false)}
                data-testid="mobile-nav-blog"
              >
                {t('nav.blog')}
              </Link>
              
              {/* Mobile Language Switcher */}
              <div className="flex gap-2 py-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      const currentPath = window.location.pathname;
                      const cleanPath = currentPath.replace(/^\/(fr|es)/, '') || '/';
                      const newPrefix = lang.code !== 'en' ? `/${lang.code}` : '';
                      navigate(`${newPrefix}${cleanPath}`);
                      setIsOpen(false);
                    }}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      language === lang.code 
                        ? 'bg-[#16a34a] text-white' 
                        : 'bg-[#f1f5f9] text-[#475569] hover:bg-[#e2e8f0]'
                    }`}
                    data-testid={`mobile-lang-${lang.code}`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>

              <a 
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#16a34a] text-white rounded-full text-sm font-medium hover:bg-[#15803d] transition-colors"
                data-testid="download-btn-mobile"
              >
                {t('nav.download')}
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
